// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Opbygning library as ReUseX embeds it (`core/buildups.json`, schema
 * v37), in the `GET /buildups` shape. The server owns the library now; this
 * copy is the fallback for a server before it (no `/buildups` route) and the
 * mock's seed. Data only, type imports only (the mock loads it under plain
 * Node), so keep it free of other imports.
 */

import type { Buildup, BuildupLayer } from '../api/types';

/** Σ t of the layers measured by thickness (core `layered_thickness`). */
export function layeredThickness(b: Pick<Buildup, 'main' | 'others'>): number {
  let sum = 0;
  for (const l of [b.main, ...b.others]) if (l.kg_m2 === undefined && l.t !== undefined && l.t < 1) sum += l.t;
  return Math.round(sum * 1e6) / 1e6;
}

const ly = (material: string, t: number, rho: number, eak: string, name?: string): BuildupLayer =>
  name === undefined ? { material, t, rho, eak } : { material, t, rho, eak, name };

function entry(b: Omit<Buildup, 'origin' | 'layered_thickness_m'>): Buildup {
  return { ...b, origin: 'library', layered_thickness_m: layeredThickness(b) };
}

export const LIBRARY_BUILDUPS: readonly Buildup[] = [
  entry({
    id: 'b1',
    category: 'ydervaeg',
    name: "Hulmur, 1960'erne, 30 cm",
    main: ly('tegl (2 × 108 mm)', 0.216, 1800, '17.01.02'),
    others: [ly('mineraluld', 0.075, 100, '17.06.04', 'Isolering, mineraluld')],
  }),
  entry({ id: 'b2', category: 'ydervaeg', name: 'Massiv mur, 1½ sten, 36 cm', main: ly('tegl', 0.348, 1800, '17.01.02'), others: [] }),
  entry({
    id: 'b3',
    category: 'ydervaeg',
    name: 'Hulmur med letbeton, 30 cm',
    main: ly('tegl', 0.108, 1800, '17.01.02'),
    others: [ly('mineraluld', 0.075, 100, '17.06.04', 'Isolering, mineraluld'), ly('letbeton', 0.1, 600, '17.01.01', 'Bagmur, letbeton')],
  }),
  entry({
    id: 'b4',
    category: 'indervaeg',
    name: 'Let skillevæg, gips, 10 cm',
    main: ly('gips (2 × 13 mm)', 0.026, 900, '17.08.02'),
    others: [ly('mineraluld', 0.07, 40, '17.06.04', 'Isolering i skillevægge')],
    note: 'Stålskelettet er ikke regnet med.',
  }),
  entry({ id: 'b5', category: 'daek', name: 'Betondæk, armeret', main: ly('beton', 1, 2400, '17.01.01'), others: [], note: 'Mængden er i m³.' }),
  entry({ id: 'b6', category: 'daek', name: 'Huldæk, beton', main: ly('beton', 1, 2300, '17.01.01'), others: [], note: 'Mængden er i m³.' }),
  entry({ id: 'b7', category: 'tag', name: 'Tagpap, 2 lag', main: { material: 'tagpap', kg_m2: 5, eak: '17.03.02' }, others: [] }),
  entry({
    id: 'b8',
    category: 'tag',
    name: 'Tagpap på træunderlag',
    main: { material: 'tagpap', kg_m2: 5, eak: '17.03.02' },
    others: [ly('træ', 0.022, 500, '17.02.01', 'Underlag, brædder')],
  }),
  entry({
    id: 'b11',
    category: 'tag',
    name: 'Betontagdæk med tagpap',
    main: ly('beton', 0.2, 2400, '17.01.01'),
    others: [{ material: 'tagpap', kg_m2: 5, eak: '17.03.02', name: 'Tagpap' }],
    note: 'Tagdækket er hovedlaget; tagpappen kan oprettes som egen type.',
  }),
  entry({ id: 'b9', category: 'indervaeg', name: 'Skillevæg, tegl, ½ sten, 11 cm', main: ly('tegl', 0.108, 1800, '17.01.02'), others: [] }),
  entry({ id: 'b10', category: 'indervaeg', name: 'Bærende indervæg, beton, 20 cm', main: ly('beton', 0.2, 2400, '17.01.01'), others: [] }),
];
